import {useState} from 'react'
import PropTypes from 'prop-types'
import useIdempotencyKey from '~/react/hooks/useIdempotencyKey'
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Checkbox,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip,
  CloseButton,
  Link,
} from '@chakra-ui/react'
import {useTranslation, Trans} from 'react-i18next'
import LogoMPointConvertAirplane from '~/react/components/Logo/LogoMPointConvertAirplane'
import {redeemPoints} from '../../api'

const RedeemPointsModal = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  partnerId,
  partnerKey,
  mldPointRateFactor,
  partnerPointRateFactor,
  partnerPointsMaximum,
  supplierId,
  membership,
}) => {
  const {t} = useTranslation(['points', 'common'])
  const [convertAmount, setConvertAmount] = useState(0)
  const [isChecked, setIsChecked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {idempotencyKey, regenerate: regenerateIdempotencyKey} =
    useIdempotencyKey(isOpen)
  const currentBalance = membership.pointsAccount.balance
  // The partner's API refuses a single accrual above its own ceiling, so the
  // amount on offer is whichever binds first: the balance, or the M-Coin that
  // converts to that ceiling. Rounding down to a whole multiple keeps the
  // conversion divisible, which the API also requires.
  const partnerCeilingInCoins =
    partnerPointsMaximum == null
      ? currentBalance
      : Math.floor(
          (partnerPointsMaximum * mldPointRateFactor) / partnerPointRateFactor,
        )
  const redeemableBalance = Math.min(currentBalance, partnerCeilingInCoins)
  const maxAmount = redeemableBalance - (redeemableBalance % mldPointRateFactor)
  const isPartnerCapped = partnerCeilingInCoins < currentBalance
  const conversionRate = mldPointRateFactor
  const partnerPoints = Math.floor(
    (convertAmount * partnerPointRateFactor) / mldPointRateFactor,
  )
  const isValid =
    convertAmount > 0 &&
    convertAmount <= maxAmount &&
    isChecked &&
    !isSubmitting

  const handleSubmit = async () => {
    setIsSubmitting(true)

    try {
      const authenticityToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content')

      const response = await redeemPoints({
        supplierId,
        redemptionPartnerId: partnerId,
        redemptionPartnerMembershipId: membership.id,
        authenticityToken,
        params: {
          pointsAccountId: membership.pointsAccount.id,
          pointsToRedeem: convertAmount,
          expectedPartnerPointsToAccrue: partnerPoints,
          idempotencyKey,
        },
      })

      const conversionData = {
        partnerKey,
        convertAmount,
        partnerPoints,
        currentBalance: currentBalance - convertAmount,
        apiResponse: response,
      }

      if (response.success) {
        regenerateIdempotencyKey()
        onSuccess(conversionData)
      } else {
        onError(conversionData)
      }
    } catch (error) {
      const conversionData = {
        partnerKey,
        convertAmount,
        partnerPoints,
        currentBalance: currentBalance - convertAmount,
        apiResponse: {success: false, error: error.message},
      }
      onError(conversionData)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" preserveScrollBarGap>
      <ModalOverlay />
      <ModalContent
        m={{base: 0, md: 'auto'}}
        borderRadius={{base: 0, md: 'md'}}
        maxW={{base: '100vw', md: '3xl'}}
        h={{base: '100vh', md: 'auto'}}
      >
        <ModalHeader fontWeight="normal">
          <HStack
            justifyContent="space-between"
            borderBottom="0.5px solid"
            pb="sm"
          >
            <>{t('points:redeemModal.redeemStart.title')}</>
            <CloseButton onClick={onClose} />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <VStack borderColor="mld.neutral.1000">
            {/* Content */}
            <VStack align="stretch" py="sm" width="100%">
              {/* Conversion Rate Badge */}
              <Box>
                <Box
                  border="1px solid"
                  borderColor="mld.neutral.500"
                  borderRadius="md"
                  display="inline-block"
                >
                  <Text textStyle="body-small" px="4px">
                    {t('points:redeemModal.redeemStart.conversionRateLabel', {
                      mldPointRateFactor,
                      partnerPointRateFactor,
                      partnerKey,
                    })}
                  </Text>
                </Box>
              </Box>

              {/* Conversion Display */}
              <Flex align="baseline" gap="xs">
                <Text
                  fontSize={{base: 'xl', md: '48px'}}
                  fontWeight="normal"
                  letterSpacing="tight"
                >
                  {convertAmount.toLocaleString()}
                  <Text
                    as="span"
                    textStyle="body-small"
                    textTransform="uppercase"
                    pl="xs"
                  >
                    {t('common:marmaladeRewardCurrency')}
                  </Text>
                </Text>
                <Box
                  display="flex"
                  alignSelf="center"
                  w="12"
                  height="fit-content"
                >
                  <LogoMPointConvertAirplane height="22px" />
                </Box>

                <Text
                  fontSize={{base: 'xl', md: '48px'}}
                  fontWeight="normal"
                  letterSpacing="tight"
                >
                  {partnerPoints.toLocaleString()}
                  <Text
                    as="span"
                    textStyle="body-small"
                    textTransform="uppercase"
                    pl="xs"
                  >
                    {t(`common:redemptionPartners.${partnerKey}.currency`)}
                  </Text>
                </Text>
              </Flex>

              {/* Slider and Balance Information */}
              <Box>
                <Slider
                  value={convertAmount}
                  onChange={(value) => setConvertAmount(value)}
                  min={0}
                  max={maxAmount}
                  step={conversionRate}
                  variant="primary"
                >
                  <SliderTrack bg="mld.neutral.500">
                    <SliderFilledTrack />
                  </SliderTrack>
                  <Tooltip
                    label={t('points:redeemModal.redeemStart.tooltip', {
                      multiple: conversionRate,
                    })}
                    placement="bottom"
                    hasArrow
                    py="4"
                    px="5"
                  >
                    <SliderThumb />
                  </Tooltip>
                </Slider>
              </Box>

              {/* Balance Information */}
              <VStack
                fontSize={{base: 'xs', md: 'sm'}}
                flexDirection="row"
                spacing="3"
                align="stretch"
                mb={isPartnerCapped ? 'xs' : '12'}
                justify="space-between"
              >
                <HStack color="mld.neutral.700">
                  <Text textStyle="body-small" letterSpacing="tight">
                    {t('points:redeemModal.redeemStart.remainingBalance')}:
                  </Text>
                  <HStack fontWeight="500" spacing="1">
                    <Text>
                      {(currentBalance - convertAmount).toLocaleString()}
                    </Text>
                    <Text
                      as="span"
                      textStyle="body-small"
                      textTransform="uppercase"
                    >
                      {t('common:marmaladeRewardCurrency')}
                    </Text>
                  </HStack>
                </HStack>
                <HStack color="mld.neutral.800" fontWeight="500">
                  <Box
                    bg="mld.neutral.200"
                    borderRadius="full"
                    display="flex"
                    px="8px"
                    py="2px"
                    gap="2"
                  >
                    <Text textDecoration="underline">
                      {t('points:redeemModal.redeemStart.maximum')}:{' '}
                      {maxAmount.toLocaleString()}
                    </Text>
                  </Box>
                </HStack>
              </VStack>

              {/* Partner Conversion Limit */}
              {isPartnerCapped && (
                <Text
                  textStyle="body-small"
                  color="mld.neutral.700"
                  mb="12"
                  data-testid="points-redemption-partner-maximum-note"
                >
                  {t('points:redeemModal.redeemStart.partnerMaximumNote', {
                    maximum: partnerPointsMaximum.toLocaleString(),
                    partnerKey,
                  })}
                </Text>
              )}

              {/* Terms Checkbox */}
              <Box bg="mld.neutral.200" p="10px" borderRadius="base" mb="6">
                <Checkbox
                  data-testid="points-redemption-terms-checkbox"
                  isChecked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  alignItems="flex-start"
                >
                  <Text
                    fontSize={{base: 'xs', md: 'sm'}}
                    color="mld.neutral.800"
                    lineHeight="relaxed"
                  >
                    <Trans
                      i18nKey={`points:redeemModal.redeemStart.partners.${partnerKey}.termsCheckbox`}
                      components={{
                        'T&C': (
                          <Link
                            variant="primary"
                            href={t(
                              `common:redemptionPartners.${partnerKey}.termsUrl`,
                            )}
                            isExternal
                          />
                        ),
                      }}
                    />
                  </Text>
                </Checkbox>
              </Box>

              {/* Action Buttons */}
              <Flex gap="24px">
                <Button
                  variant="primary"
                  borderRadius="full"
                  isDisabled={!isValid}
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                >
                  {t('points:redeemModal.buttons.redeemNow')}
                </Button>
                <Button
                  border="1px solid"
                  variant="secondary"
                  borderRadius="full"
                  onClick={onClose}
                >
                  {t('points:redeemModal.buttons.cancel')}
                </Button>
              </Flex>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

RedeemPointsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  mldPointRateFactor: PropTypes.number.isRequired,
  partnerPointRateFactor: PropTypes.number.isRequired,
  partnerPointsMaximum: PropTypes.number,
  partnerId: PropTypes.string.isRequired,
  partnerKey: PropTypes.string.isRequired,
  supplierId: PropTypes.string.isRequired,
  membership: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    pointsAccount: PropTypes.shape({
      id: PropTypes.string.isRequired,
      balance: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
}

RedeemPointsModal.defaultProps = {
  partnerPointsMaximum: null,
}

export default RedeemPointsModal
